import { useCallback } from 'react';
import { usePageInstanceId } from '../../context/PageInstanceIdProvider';
import DataSourceHelper from '../../helper/DataSourceHelper';
import { getIsDataSourceAnEntityType } from '../../utils/entityDataSource';
import {
  useAppDataSourceRecordStore,
  useDataSourceRecordStoreManager,
} from './DataSourceRecordStoreProvider';

export const useGetPageDataSourceRecordStoreState = () => {
  const manager = useDataSourceRecordStoreManager();
  const instanceId = usePageInstanceId();
  if (instanceId) {
    return { getPageDataSourceRecordStoreState: manager.getStore(instanceId).getState };
  }
  return { getPageDataSourceRecordStoreState: manager.baseStore.getState };
};

export const useGetAppDataSourceRecordStoreState = () => {
  const getAppDataSourceRecordStoreState = useAppDataSourceRecordStore().getState;
  return { getAppDataSourceRecordStoreState };
};

export const useGetDataSourceRecordStoreState = () => {
  const { getPageDataSourceRecordStoreState } = useGetPageDataSourceRecordStoreState();
  const { getAppDataSourceRecordStoreState } = useGetAppDataSourceRecordStoreState();

  const getDataSourceRecordStoreState = useCallback(
    (dataSourceId: string) => {
      const pageDataSources = getPageDataSourceRecordStoreState().dataSources;
      const appDataSources = getAppDataSourceRecordStoreState().dataSources;

      if (getIsDataSourceAnEntityType(dataSourceId)) {
        return getPageDataSourceRecordStoreState();
      }

      const dataSourceRecord = pageDataSources[dataSourceId] || appDataSources[dataSourceId];
      const isAppDataSource = DataSourceHelper.isAppDataSource(dataSourceRecord);
      return isAppDataSource
        ? getAppDataSourceRecordStoreState()
        : getPageDataSourceRecordStoreState();
    },
    [getPageDataSourceRecordStoreState, getAppDataSourceRecordStoreState],
  );

  return { getDataSourceRecordStoreState };
};

/**
 * Returns a callback that returns the unsaved data source from the store if it exists,
 * otherwise returns the saved data source. checks at both app level and page level.
 * This is useful in event callback where we need to get the latest copy of the data source from the store.
 */
export function useGetUnsavedDataSourceRecord() {
  const { getDataSourceRecordStoreState } = useGetDataSourceRecordStoreState();
  const getUnsavedDataSourceRecord = useCallback(
    (dataSourceId: string) => {
      const { dataSources, unsavedDataSources } = getDataSourceRecordStoreState(dataSourceId);
      return unsavedDataSources[dataSourceId] || dataSources[dataSourceId];
    },
    [getDataSourceRecordStoreState],
  );

  return { getUnsavedDataSourceRecord };
}

/**
 * Returns a callback that returns the saved data source record from the store if it exists,
 * checks at both app level and page level.
 */
export function useGetDataSourceRecord() {
  const { getDataSourceRecordStoreState } = useGetDataSourceRecordStoreState();

  const getDataSourceRecord = useCallback(
    (dataSourceId: string) => {
      const { dataSources } = getDataSourceRecordStoreState(dataSourceId);
      return dataSources[dataSourceId];
    },
    [getDataSourceRecordStoreState],
  );

  return { getDataSourceRecord };
}

export function useGetMergedDataSourceRecords() {
  const { getPageDataSourceRecordStoreState } = useGetPageDataSourceRecordStoreState();
  const { getAppDataSourceRecordStoreState } = useGetAppDataSourceRecordStoreState();

  const getMergedDataSourceRecords = useCallback(() => {
    const { dataSources, unsavedDataSources } = getPageDataSourceRecordStoreState();
    const { dataSources: appDataSources, unsavedDataSources: appUnsavedDataSources } =
      getAppDataSourceRecordStoreState();
    return { ...appDataSources, ...dataSources, ...appUnsavedDataSources, ...unsavedDataSources };
  }, [getPageDataSourceRecordStoreState, getAppDataSourceRecordStoreState]);

  return { getMergedDataSourceRecords };
}

export function useGetAppDataSourceRecords() {
  const { getAppDataSourceRecordStoreState } = useGetAppDataSourceRecordStoreState();
  const getAppDataSourceRecords = useCallback(() => {
    return getAppDataSourceRecordStoreState().dataSources;
  }, [getAppDataSourceRecordStoreState]);
  return { getAppDataSourceRecords };
}

/**
 * Returns a callback that returns the data source records from the store.
 * this has both the page and app data source records.
 */
export function useGetDataSourceRecords() {
  const { getPageDataSourceRecordStoreState } = useGetPageDataSourceRecordStoreState();
  const { getAppDataSourceRecordStoreState } = useGetAppDataSourceRecordStoreState();

  const getDataSourceRecords = useCallback(() => {
    const { dataSources } = getPageDataSourceRecordStoreState();
    const { dataSources: appDataSources } = getAppDataSourceRecordStoreState();
    return { ...appDataSources, ...dataSources };
  }, [getPageDataSourceRecordStoreState, getAppDataSourceRecordStoreState]);

  return { getDataSourceRecords };
}

export function useGetAppDataSourceExists() {
  const { getAppDataSourceRecordStoreState } = useGetAppDataSourceRecordStoreState();
  const getAppDataSourceExists = useCallback(
    (dataSourceId: string) => !!getAppDataSourceRecordStoreState().dataSources[dataSourceId],
    [getAppDataSourceRecordStoreState],
  );

  return { getAppDataSourceExists };
}

function useGetPageDataSourceExists() {
  const { getPageDataSourceRecordStoreState } = useGetPageDataSourceRecordStoreState();
  const getPageDataSourceExists = useCallback(
    (dataSourceId: string) =>
      !!getPageDataSourceRecordStoreState().dataSources[dataSourceId] ||
      getIsDataSourceAnEntityType(dataSourceId),
    [getPageDataSourceRecordStoreState],
  );

  return { getPageDataSourceExists };
}

export const useGetDataSourceExists = () => {
  const { getAppDataSourceExists } = useGetAppDataSourceExists();
  const { getPageDataSourceExists } = useGetPageDataSourceExists();

  const getDataSourceExists = useCallback(
    (dataSourceId: string) => {
      return getPageDataSourceExists(dataSourceId) || getAppDataSourceExists(dataSourceId);
    },
    [getPageDataSourceExists, getAppDataSourceExists],
  );

  return { getDataSourceExists };
};

export const useGetPageDataSourceRecordStoreActions = () => {
  const { getPageDataSourceRecordStoreState } = useGetPageDataSourceRecordStoreState();
  return getPageDataSourceRecordStoreState().actions;
};

export const useGetAppDataSourceRecordStoreActions = () => {
  const { getAppDataSourceRecordStoreState } = useGetAppDataSourceRecordStoreState();
  return getAppDataSourceRecordStoreState().actions;
};

export const useGetDataSourceRecordStoreActions = () => {
  const { getDataSourceRecordStoreState } = useGetDataSourceRecordStoreState();

  const getDataSourceRecordStoreActions = useCallback(
    (dataSourceId: string) => {
      const { actions } = getDataSourceRecordStoreState(dataSourceId);
      return actions;
    },
    [getDataSourceRecordStoreState],
  );

  return { getDataSourceRecordStoreActions };
};
